import { useCallback, useSyncExternalStore } from 'react';
import { useCurrentProject } from './useCurrentProject';
import {
  isFeatureEnabled,
  subscribeToFeatureFlagChanges,
  type FeatureFlagName,
} from '@/featureFlags';

/**
 * Hook wrapper around `isFeatureEnabled` that auto-provides the region
 * from the current route's locationName param and re-renders the
 * consuming component when:
 *   - the user toggles a flag in Settings -> Experimental (emits
 *     `otto:feature-flags-changed`)
 *   - another tab updates the localStorage overrides key (`storage`)
 *
 * Without this subscription, components that gate UI on
 * `useFeatureFlag(...)` would only pick up a new override on their next
 * unrelated re-render, which is what the PR description ("effective
 * immediately") promised to avoid.
 *
 * Use in components rendered inside the Router. For module-scope or
 * non-component code, use `isFeatureEnabled()` directly.
 */
export function useFeatureFlag(flag: FeatureFlagName): boolean {
  const { locationName } = useCurrentProject();

  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeToFeatureFlagChanges(onStoreChange),
    [],
  );

  // The "snapshot" is the current evaluation of the flag. Recomputing
  // here means `useSyncExternalStore` returns the latest value on every
  // subscriber notification and React skips re-renders when the result
  // is referentially equal (booleans, so Object.is works perfectly).
  const getSnapshot = useCallback(
    () => isFeatureEnabled(flag, locationName),
    [flag, locationName],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
